import { useQuery } from "@tanstack/react-query";
import { type Dayjs } from "dayjs";

import { ClientActivityWithCalculatedScore } from "@fitness-app/data-models/domain/services/ClientActivityWithCalculatedScore";
import {
  type ClientActivity,
  type ClientActivityWithScore,
  type ClientMonthActivity,
} from "@fitness-app/data-models/entities/ClientActivity";

import { traineeKeys } from "~/modules/Trainee/TraineeProfile/scope";
import { supabase } from "~/store/initializeStore";

/**
 * Admin-side activity hooks (React Query). Self-contained — admin doesn't depend on client-shared, so the
 * Supabase query + score transform are inlined here using the admin Supabase singleton. Replaces the Redux
 * `fetchClientActivity` / `fetchDailyActivities` thunks so the data refetches on window focus.
 */
const STALE_TIME = 30_000;

export const useClientActivityReport = (traineeId: string | null | undefined, enabled = true) =>
  useQuery({
    queryKey: [...traineeKeys.all(traineeId ?? ""), "clientActivity", "report"],
    enabled: !!traineeId && enabled,
    staleTime: STALE_TIME,
    queryFn: async (): Promise<ClientActivityWithScore | null> => {
      const { data, error } = await supabase
        .from("client_activity")
        .select("*")
        .eq("traineeId", traineeId!)
        .maybeSingle<ClientActivity>();

      if (error) {
        throw new Error(error.message);
      }

      return data ? new ClientActivityWithCalculatedScore(data).calculateScore().getReport() : null;
    },
  });

export const useClientDailyActivities = (
  traineeId: string | null | undefined,
  start: Dayjs | null,
  end: Dayjs | null,
) => {
  const startMonth = start ? start.format("YYYY-MM") : null;
  const endMonth = end ? end.format("YYYY-MM") : null;

  return useQuery({
    queryKey: [...traineeKeys.all(traineeId ?? ""), "clientActivity", "daily", startMonth, endMonth],
    enabled: !!traineeId && !!startMonth && !!endMonth,
    keepPreviousData: true,
    staleTime: STALE_TIME,
    queryFn: async (): Promise<ClientMonthActivity[]> => {
      const { data, error } = await supabase
        .from("client_month_activity")
        .select("*")
        .eq("traineeId", traineeId!)
        .gte("monthYear", startMonth!)
        .lte("monthYear", endMonth!)
        .returns<ClientMonthActivity[]>();

      if (error) {
        throw new Error(error.message);
      }

      return data;
    },
  });
};
